"use client";

import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { SidebarProvider } from "@/components/layout/SidebarProvider";
import { Sidebar } from "@/components/layout/Sidebar";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { CommsProvider } from "@/components/comms-panel/CommsProvider";
import { CommsPanel } from "@/components/comms-panel/CommsPanel";
import { Toaster } from "@/components/ui/sonner";

function AuthGuard({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/api/auth/session", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.user) {
          if (data.user.active === false) {
            navigate("/login?pending=1", { replace: true });
          } else {
            setAuthenticated(true);
          }
        } else {
          navigate("/login", { replace: true });
        }
      })
      .catch(() => {
        navigate("/login", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!authenticated) return null;

  return <>{children}</>;
}

export default function AppLayout() {
  return (
    <AuthGuard>
      <CommsProvider>
        <SidebarProvider>
          <div className="flex h-screen flex-col overflow-hidden bg-[#f7f9fb]">
            <ImpersonationBanner />
            <div className="flex flex-1 overflow-hidden">
              <Sidebar />
              <main className="flex-1 overflow-y-auto min-w-0">
                <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
                  <Outlet />
                </div>
              </main>
              <CommsPanel />
            </div>
            <Toaster position="top-right" richColors closeButton />
          </div>
        </SidebarProvider>
      </CommsProvider>
    </AuthGuard>
  );
}
